import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { fmtMoney, fmtDate } from "@/lib/format";
import buildSubDealerTaxInvoiceHtml from "@/components/subDealerTaxInvoiceHtml";
import {
  Storefront,
  Plus,
  Trash,
  ArrowLeft,
  FileText,
  Gear,
  Printer,
  Eye,
  X as XIcon,
  Check,
} from "@phosphor-icons/react";

// ============================================================
// Sub-Dealers list
// ============================================================
function SubDealersList() {
  const nav = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sub-dealers/accounts");
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalOutstanding = useMemo(
    () => accounts.reduce((s, a) => s + (a.balance || 0), 0),
    [accounts],
  );

  return (
    <div data-testid="sub-dealers-list">
      <PageHeader
        eyebrow="10 / Sub-Dealers"
        title="Authorised Dealer Accounts"
        subtitle="B2B ledgers, invoices and outstanding balances per sub-dealer."
        actions={
          <button
            data-testid="manage-txn-types"
            onClick={() => nav("/sub-dealers/transaction-types")}
            className="px-3 py-2 border border-canvas-line text-xs font-semibold hover:border-ink inline-flex items-center gap-2"
          >
            <Gear size={13} weight="bold" /> Transaction Types
          </button>
        }
      />
      <div className="px-8 py-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-ink p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              Total Outstanding (All Dealers)
            </div>
            <div className="font-display text-2xl font-bold mt-2 tabular-nums">
              {fmtMoney(totalOutstanding)}
            </div>
          </div>
          <div className="border border-canvas-line bg-canvas-card p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              Active Sub-Dealers
            </div>
            <div className="font-display text-2xl font-bold mt-2 tabular-nums">
              {accounts.length}
            </div>
          </div>
        </div>

        <div className="bg-canvas-card border border-canvas-line overflow-x-auto">
          <table className="w-full text-sm" data-testid="sub-dealers-table">
            <thead className="border-b border-canvas-line bg-canvas-panel">
              <tr>
                {["Sub-Dealer", "Branch Code", "Last Activity", "Last Type", "Outstanding ₹", ""].map(
                  (h) => (
                    <th
                      key={h}
                      className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center font-mono text-xs text-ink-subtle">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && accounts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center font-mono text-xs text-ink-subtle">
                    No sub-dealers yet. Mark a branch type as "Sub-Dealer" in Branch Types, then add
                    branches under it.
                  </td>
                </tr>
              )}
              {accounts.map((a) => (
                <tr
                  key={a.branch.id}
                  data-testid={`sub-dealer-row-${a.branch.id}`}
                  className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel cursor-pointer"
                  onClick={() => nav(`/sub-dealers/${a.branch.id}`)}
                >
                  <td className="px-3 py-3 font-semibold">{a.branch.name}</td>
                  <td className="px-3 py-3 font-mono text-[11px]">{a.branch.code}</td>
                  <td className="px-3 py-3 font-mono text-[11px]">{a.last_entry_date ? fmtDate(a.last_entry_date) : "—"}</td>
                  <td className="px-3 py-3 font-mono text-[10px] text-ink-muted">{a.last_entry_type || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums font-semibold">
                    {fmtMoney(a.balance || 0)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                      Open →
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Sub-Dealer Detail (Ledger + Invoice)
// ============================================================
function AddEntryDialog({ branchId, txnTypes, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    txn_type_id: txnTypes[0]?.id || "",
    reference_number: "",
    amount: "",
    comments: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a positive amount.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/sub-dealers/${branchId}/ledger`, {
        date: form.date,
        txn_type_id: form.txn_type_id,
        reference_number: form.reference_number,
        amount: amt,
        comments: form.comments,
      });
      toast.success("Entry added");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="add-entry-dialog"
        className="bg-canvas-card border-2 border-ink p-6 w-full max-w-md space-y-4"
      >
        <h3 className="font-display text-lg font-semibold">Add Ledger Entry</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Date</label>
            <input
              type="date"
              required
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="entry-date"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="entry-amount"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Type</label>
          <select
            required
            value={form.txn_type_id}
            onChange={(e) => setForm({ ...form, txn_type_id: e.target.value })}
            data-testid="entry-type"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
          >
            {txnTypes.map((t) => (
              <option key={t.id} value={t.id}>
                [{t.direction.toUpperCase()[0]}] {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Reference (invoice / UTR / etc.)</label>
          <input
            value={form.reference_number}
            onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
            data-testid="entry-ref"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Comments</label>
          <textarea
            value={form.comments}
            onChange={(e) => setForm({ ...form, comments: e.target.value })}
            data-testid="entry-comments"
            rows={2}
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 border border-canvas-line text-xs">Cancel</button>
          <button type="submit" disabled={saving} data-testid="entry-submit" className="px-3 py-1.5 bg-ink text-white text-xs font-semibold">
            {saving ? "Saving…" : "Add Entry"}
          </button>
        </div>
      </form>
    </div>
  );
}

function InvoiceDialog({ branchId, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    gst_type: "intra",
    cgst_pct: 9,
    sgst_pct: 9,
    igst_pct: 18,
    notes: "",
    lines: [{ model: "", vin: "", billing_price: "", mandatory_landing: "", quantity: 1 }],
  });
  const [saving, setSaving] = useState(false);

  const updateLine = (i, k, v) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, j) => (i === j ? { ...l, [k]: v } : l)) }));
  const addLine = () =>
    setForm((f) => ({ ...f, lines: [...f.lines, { model: "", vin: "", billing_price: "", mandatory_landing: "", quantity: 1 }] }));
  const removeLine = (i) =>
    setForm((f) => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));

  const subtotal = form.lines.reduce(
    (s, l) => s + (Number(l.billing_price) || 0) * (Number(l.quantity) || 1) + (Number(l.mandatory_landing) || 0) * (Number(l.quantity) || 1),
    0,
  );
  const gstAmt =
    form.gst_type === "intra"
      ? subtotal * (Number(form.cgst_pct) + Number(form.sgst_pct)) / 100
      : subtotal * Number(form.igst_pct) / 100;
  const grand = subtotal + gstAmt;

  const submit = async (e) => {
    e.preventDefault();
    const validLines = form.lines.filter((l) => l.model && Number(l.billing_price) > 0);
    if (validLines.length === 0) {
      toast.error("Add at least one line with model + price.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/sub-dealers/${branchId}/invoices`, {
        ...form,
        lines: validLines.map((l) => ({
          model: l.model,
          vin: l.vin || "",
          billing_price: Number(l.billing_price) || 0,
          mandatory_landing: Number(l.mandatory_landing) || 0,
          quantity: Number(l.quantity) || 1,
        })),
      });
      toast.success(`Invoice ${data.invoice_no} issued`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="invoice-dialog"
        className="bg-canvas-card border-2 border-ink p-6 w-full max-w-3xl space-y-4 my-8"
      >
        <h3 className="font-display text-lg font-semibold">Generate Tax Invoice</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Date</label>
            <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="inv-date" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">GST Type</label>
            <select value={form.gst_type} onChange={(e) => setForm({ ...form, gst_type: e.target.value })} data-testid="inv-gst-type" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
              <option value="intra">Intra (CGST + SGST)</option>
              <option value="inter">Inter (IGST)</option>
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">
              {form.gst_type === "intra" ? "CGST + SGST %" : "IGST %"}
            </label>
            <div className="font-mono text-sm pt-2">
              {form.gst_type === "intra" ? `${form.cgst_pct}% + ${form.sgst_pct}%` : `${form.igst_pct}%`}
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Line Items</label>
            <button type="button" onClick={addLine} data-testid="inv-add-line" className="text-xs font-mono uppercase tracking-wider text-ink-muted hover:text-ink">
              + Add Line
            </button>
          </div>
          <div className="border border-canvas-line">
            <table className="w-full text-xs">
              <thead className="bg-canvas-panel">
                <tr>
                  {["Model", "Frame No (VIN)", "Billing ₹", "Mandatory ₹", "Qty", "Total ₹", ""].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 font-mono text-[9px] uppercase text-ink-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => {
                  const lt = (Number(l.billing_price) + Number(l.mandatory_landing)) * (Number(l.quantity) || 1);
                  return (
                    <tr key={i} className="border-t border-canvas-line" data-testid={`inv-line-${i}`}>
                      <td className="px-1 py-1">
                        <input value={l.model} onChange={(e) => updateLine(i, "model", e.target.value)} placeholder="e.g. RAIDER DISC" className="w-full px-2 py-1 bg-canvas border border-canvas-line text-xs" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.vin} onChange={(e) => updateLine(i, "vin", e.target.value.toUpperCase())} className="w-full px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" step="0.01" value={l.billing_price} onChange={(e) => updateLine(i, "billing_price", e.target.value)} className="w-full px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" step="0.01" value={l.mandatory_landing} onChange={(e) => updateLine(i, "mandatory_landing", e.target.value)} className="w-full px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono" />
                      </td>
                      <td className="px-1 py-1 w-16">
                        <input type="number" min="1" value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="w-full px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono" />
                      </td>
                      <td className="px-2 py-1 font-mono tabular-nums">{fmtMoney(lt)}</td>
                      <td className="px-2 py-1">
                        <button type="button" onClick={() => removeLine(i)} className="text-ink-subtle hover:text-destructive"><Trash size={11} weight="bold" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border border-canvas-line bg-canvas-panel p-3 font-mono text-xs space-y-1">
          <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{fmtMoney(subtotal)}</span></div>
          <div className="flex justify-between text-ink-muted"><span>GST ({form.gst_type === "intra" ? `${form.cgst_pct + form.sgst_pct}%` : `${form.igst_pct}%`})</span><span className="tabular-nums">{fmtMoney(gstAmt)}</span></div>
          <div className="flex justify-between font-semibold text-base border-t border-canvas-line pt-1 mt-1"><span>Grand Total</span><span className="tabular-nums" data-testid="inv-grand-total">{fmtMoney(grand)}</span></div>
        </div>

        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Notes</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 border border-canvas-line text-xs">Cancel</button>
          <button type="submit" disabled={saving} data-testid="inv-submit" className="px-3 py-1.5 bg-ink text-white text-xs font-semibold">
            {saving ? "Issuing…" : "Generate Invoice"}
          </button>
        </div>
      </form>
    </div>
  );
}

function BulkVehicleDialog({ branchId, onClose, onSaved }) {
  const [allVehicles, setAllVehicles] = useState([]);
  const [adPkgCount, setAdPkgCount] = useState(0);
  const [selected, setSelected] = useState({});  // {vehicle_id: {checked, override}}
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [gstType, setGstType] = useState("intra");
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Two-step flow: operator builds the selection, then clicks "Review" to see
  // the preview drawer (with bucket breakdown + totals) before confirming.
  // Prevents accidental bulk invoicing of the wrong batch.
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sub-dealers/${branchId}/billable-vehicles`);
        setAllVehicles(data.vehicles || []);
        setAdPkgCount(data.ad_packages_count || 0);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return allVehicles;
    return allVehicles.filter(v =>
      (v.vin || "").toLowerCase().includes(q) ||
      (v.package_name || "").toLowerCase().includes(q) ||
      (v.model_category || "").toLowerCase().includes(q) ||
      (v.color || "").toLowerCase().includes(q)
    );
  }, [allVehicles, search]);

  const toggleAll = () => {
    if (filtered.every(v => selected[v.id]?.checked)) {
      setSelected({});
    } else {
      const next = { ...selected };
      filtered.forEach(v => { next[v.id] = { checked: true, override: selected[v.id]?.override ?? "" }; });
      setSelected(next);
    }
  };

  const setLine = (id, patch) =>
    setSelected(s => ({ ...s, [id]: { ...(s[id] || {}), ...patch } }));

  const selectedRows = filtered.filter(v => selected[v.id]?.checked);
  const subtotal = selectedRows.reduce((s, v) => {
    const ov = selected[v.id]?.override;
    const p = ov === "" || ov == null ? Number(v.ad_price) || 0 : Number(ov);
    return s + p;
  }, 0);
  const gstAmt = gstType === "intra" ? subtotal * 0.18 : subtotal * 0.18;  // 9%+9% or 18%
  // OEM company discount preview — only counted when the line uses the
  // AD price (override_price bypasses the discount server-side).
  const companyDiscount = selectedRows.reduce((s, v) => {
    const ov = selected[v.id]?.override;
    if (ov !== "" && ov != null) return s;  // overridden — no discount
    return s + (Number(v.ad_company_discount) || 0);
  }, 0);
  const grand = subtotal + gstAmt - companyDiscount;

  const openPreview = () => {
    if (selectedRows.length === 0) { toast.error("Select at least one vehicle"); return; }
    const missing = selectedRows.filter(v => {
      const ov = selected[v.id]?.override;
      const p = ov === "" || ov == null ? Number(v.ad_price) || 0 : Number(ov);
      return p <= 0;
    });
    if (missing.length > 0) {
      toast.error(`${missing.length} vehicle(s) have no price. Enter override or map an AD package to that model.`);
      return;
    }
    setShowPreview(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/sub-dealers/${branchId}/invoices/bulk-vehicles`, {
        date, gst_type: gstType, notes,
        vehicles: selectedRows.map(v => ({
          vehicle_id: v.id,
          override_price: selected[v.id]?.override === "" || selected[v.id]?.override == null ? null : Number(selected[v.id].override),
        })),
      });
      toast.success(`${data.invoice_no} issued — ${selectedRows.length} vehicles moved to sub-dealer`);
      setShowPreview(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="bulk-vehicle-dialog" className="bg-canvas-card border-2 border-ink p-6 w-full max-w-5xl space-y-4 my-8">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Bulk Vehicle Invoice — Bill from Stock Yard</h3>
          <span className="font-mono text-[10px] text-ink-muted">{adPkgCount} AD packages mapped · {filtered.length} vehicles in stock yard</span>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="bulk-inv-date" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">GST</label>
            <select value={gstType} onChange={e => setGstType(e.target.value)} data-testid="bulk-inv-gst" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
              <option value="intra">Intra (9%+9%)</option>
              <option value="inter">Inter (18%)</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Search VIN / Model_Category / Package / Color</label>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Type to filter…" data-testid="bulk-inv-search" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono" />
          </div>
        </div>

        <div className="border border-canvas-line max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="bulk-vehicles-table">
            <thead className="bg-canvas-panel sticky top-0">
              <tr>
                <th className="px-2 py-2"><input type="checkbox" checked={filtered.length > 0 && filtered.every(v => selected[v.id]?.checked)} onChange={toggleAll} data-testid="bulk-toggle-all" /></th>
                {["VIN", "Model_Category", "Package", "Color", "Engine No", "AD Price ₹", "Override ₹"].map(h => (
                  <th key={h} className="text-left px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-2 py-8 text-center font-mono text-[11px] text-ink-subtle">No available vehicles in Stock Yard.</td></tr>
              )}
              {filtered.map(v => (
                <tr key={v.id} data-testid={`bulk-row-${v.id}`} className="border-t border-canvas-line hover:bg-canvas-panel">
                  <td className="px-2 py-1"><input type="checkbox" checked={!!selected[v.id]?.checked} onChange={e => setLine(v.id, { checked: e.target.checked })} /></td>
                  <td className="px-2 py-1 font-mono">{v.vin}</td>
                  <td className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{v.model_category || "—"}</td>
                  <td className="px-2 py-1">{v.package_name || "—"}</td>
                  <td className="px-2 py-1">{v.color || "—"}</td>
                  <td className="px-2 py-1 font-mono text-[10px]">{v.engine_no || "—"}</td>
                  <td className="px-2 py-1 font-mono tabular-nums">
                    {v.has_ad_price ? fmtMoney(v.ad_price) : <span className="text-destructive">no AD price</span>}
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" step="0.01" placeholder={v.has_ad_price ? "" : "required"} value={selected[v.id]?.override ?? ""} onChange={e => setLine(v.id, { override: e.target.value })} className="w-24 px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-canvas-line bg-canvas-panel p-3 font-mono text-xs space-y-1">
          <div className="flex justify-between"><span>Selected: {selectedRows.length} vehicle(s)</span><span>Subtotal {fmtMoney(subtotal)}</span></div>
          <div className="flex justify-between text-ink-muted"><span>GST (18% effective)</span><span>{fmtMoney(gstAmt)}</span></div>
          {companyDiscount > 0 && (
            <div className="flex justify-between text-emerald-700" data-testid="bulk-company-discount">
              <span>Less: Company Discount</span><span>- {fmtMoney(companyDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold text-base border-t border-canvas-line pt-1"><span>Grand Total</span><span data-testid="bulk-grand-total">{fmtMoney(grand)}</span></div>
        </div>

        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" rows={2} className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 border border-canvas-line text-xs">Cancel</button>
          <button type="button" onClick={openPreview} disabled={selectedRows.length === 0} data-testid="bulk-review" className="px-3 py-1.5 bg-ink text-white text-xs font-semibold disabled:opacity-50">
            Review {selectedRows.length} Vehicles →
          </button>
        </div>
      </div>
      {showPreview && (
        <BulkInvoicePreviewDialog
          selectedRows={selectedRows}
          selected={selected}
          date={date}
          gstType={gstType}
          notes={notes}
          subtotal={subtotal}
          onBack={() => setShowPreview(false)}
          onConfirm={submit}
          saving={saving}
        />
      )}
    </div>
  );
}

/**
 * Two-step confirm for the Bulk Vehicle Invoice flow.
 *
 * Shows everything the operator's about to commit — vehicles, per-line
 * resolved prices, GST math (using the same bucket logic the backend will
 * apply), grand total — and asks for an explicit "Confirm & Issue" click.
 * The "Back" button returns to the selection screen so the operator can
 * fix mistakes without losing their selection.
 */
function BulkInvoicePreviewDialog({ selectedRows, selected, date, gstType, notes, subtotal, onBack, onConfirm, saving }) {
  // Approximate GST per the bucket rules (ex-showroom + other → on top;
  // accessories → already inclusive). We don't have the bucket split in
  // billable-vehicles, so we show the worst-case (entire `ad_price` as
  // taxable). Critically, we use each VEHICLE's own resolved cgst_pct /
  // igst_pct (from its AD package) instead of a hardcoded 9/9/18 — this
  // is what makes ELECTRIC vehicles (5% + 5%) preview correctly.
  let cgstAmt = 0;
  let sgstAmt = 0;
  let igstAmt = 0;
  const rateBuckets = { cgst: {}, sgst: {}, igst: {} };
  for (const v of selectedRows) {
    const ov = selected[v.id]?.override;
    const taxable = ov === "" || ov == null ? Number(v.ad_price) || 0 : Number(ov);
    if (taxable <= 0) continue;
    if (gstType === "intra") {
      const cgst = Number(v.cgst_pct) || 0;
      const cAmt = (taxable * cgst) / 100;
      cgstAmt += cAmt;
      sgstAmt += cAmt;
      rateBuckets.cgst[cgst] = (rateBuckets.cgst[cgst] || 0) + cAmt;
      rateBuckets.sgst[cgst] = (rateBuckets.sgst[cgst] || 0) + cAmt;
    } else {
      // Inter-state. Use package's igst_pct; fall back to cgst_pct × 2
      // (intra rate doubled) when the package only carries the intra rate.
      const igst = Number(v.igst_pct) || (Number(v.cgst_pct) || 0) * 2;
      const iAmt = (taxable * igst) / 100;
      igstAmt += iAmt;
      rateBuckets.igst[igst] = (rateBuckets.igst[igst] || 0) + iAmt;
    }
  }
  const grand = subtotal + cgstAmt + sgstAmt + igstAmt;
  // Build a short rate summary string for the header (e.g. "CGST 9%/5% + SGST 9%/5%").
  const fmtRates = (bucket) => {
    const ks = Object.keys(bucket).filter((k) => bucket[k] > 0);
    if (ks.length === 0) return "0%";
    return ks.map((k) => `${k}%`).join(" / ");
  };
  const headerRateSummary = gstType === "intra"
    ? `Intra-state (CGST ${fmtRates(rateBuckets.cgst)} + SGST ${fmtRates(rateBuckets.sgst)})`
    : `Inter-state (IGST ${fmtRates(rateBuckets.igst)})`;
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-start justify-center p-4 overflow-y-auto" data-testid="bulk-preview-dialog">
      <div className="bg-canvas-card border-2 border-ink p-6 w-full max-w-4xl space-y-4 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-canvas-line pb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">Step 2 / 2 · Review & Confirm</div>
            <div className="font-display text-xl font-bold mt-1">Confirm Bulk Vehicle Invoice</div>
            <div className="text-xs text-ink-muted mt-1">
              {selectedRows.length} vehicles · {date} · {headerRateSummary}
            </div>
          </div>
        </div>

        {/* Vehicles to be invoiced */}
        <div className="border border-canvas-line max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
              <tr>
                {["#", "VIN", "Model_Category · Package · Color", "Price (₹)"].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedRows.map((v, i) => {
                const ov = selected[v.id]?.override;
                const p = ov === "" || ov == null ? Number(v.ad_price) || 0 : Number(ov);
                return (
                  <tr key={v.id} className="border-b border-canvas-line last:border-b-0">
                    <td className="px-2 py-1.5 font-mono">{i + 1}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{v.vin || "—"}</td>
                    <td className="px-2 py-1.5">{[v.model_category, v.package_name, v.color].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-2 py-1.5 font-mono tabular-nums font-semibold">
                      {fmtMoney(p)}
                      {ov !== "" && ov != null && <span className="block text-[9px] text-ink-muted">override</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals (approximate — backend recomputes by bucket) */}
        <div className="border border-canvas-line p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Taxable (approx)</div>
            <div className="font-mono tabular-nums font-semibold mt-1">{fmtMoney(subtotal)}</div>
          </div>
          {gstType === "intra" ? (
            <>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">CGST {fmtRates(rateBuckets.cgst)}</div>
                <div className="font-mono tabular-nums font-semibold mt-1">{fmtMoney(cgstAmt)}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">SGST {fmtRates(rateBuckets.sgst)}</div>
                <div className="font-mono tabular-nums font-semibold mt-1">{fmtMoney(sgstAmt)}</div>
              </div>
            </>
          ) : (
            <div className="col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">IGST {fmtRates(rateBuckets.igst)}</div>
              <div className="font-mono tabular-nums font-semibold mt-1">{fmtMoney(igstAmt)}</div>
            </div>
          )}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Grand Total (approx)</div>
            <div className="font-mono tabular-nums font-bold text-lg mt-1" data-testid="bulk-preview-grand-total">{fmtMoney(grand)}</div>
          </div>
        </div>

        {notes && (
          <div className="text-xs text-ink-muted border border-canvas-line p-3 bg-canvas-panel">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Notes</div>
            {notes}
          </div>
        )}

        <div className="text-[11px] text-ink-muted">
          <strong>Heads-up</strong> — clicking Confirm will (1) generate the AD invoice number, (2) post the debit to the sub-dealer ledger, (3) move these {selectedRows.length} vehicles out of Stock Yard into the sub-dealer&apos;s branch. The taxable & GST totals above are computed from the AD price; the actual invoice will split per-bucket (ex-showroom + accessories + other) — totals may shift slightly.
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-canvas-line pt-3">
          <button type="button" onClick={onBack} disabled={saving} data-testid="bulk-preview-back" className="px-4 py-1.5 border border-canvas-line text-xs disabled:opacity-50">
            ← Back
          </button>
          <button type="button" onClick={onConfirm} disabled={saving} data-testid="bulk-preview-confirm" className="px-4 py-1.5 bg-ink text-white text-xs font-semibold disabled:opacity-50">
            {saving ? "Issuing…" : `Confirm & Issue Invoice`}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubDealerDetail() {
  const { branchId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_superuser;
  const nav = useNavigate();
  const [ledger, setLedger] = useState({ rows: [], opening_balance: 0, closing_balance: 0, debit_total: 0, credit_total: 0 });
  const [txnTypes, setTxnTypes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [branch, setBranch] = useState(null);
  const [tab, setTab] = useState("ledger");
  const [showEntry, setShowEntry] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [previewInvoice, setPreviewInvoice] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      // Use allSettled so a single 4xx (e.g., branch lookup) doesn't blank the page.
      const [lRes, tRes, iRes, accRes] = await Promise.allSettled([
        api.get(`/sub-dealers/${branchId}/ledger`, { params }),
        api.get("/sub-dealers/transaction-types", { params: { active_only: true } }),
        api.get(`/sub-dealers/${branchId}/invoices`),
        api.get(`/sub-dealers/accounts`),
      ]);
      if (lRes.status === "fulfilled") setLedger(lRes.value.data);
      if (tRes.status === "fulfilled") setTxnTypes(tRes.value.data || []);
      if (iRes.status === "fulfilled") setInvoices(iRes.value.data || []);
      if (accRes.status === "fulfilled") {
        const accounts = accRes.value.data?.accounts || [];
        const hit = accounts.find((a) => a.branch.id === branchId);
        if (hit) setBranch(hit.branch);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [branchId, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this ledger entry?")) return;
    try {
      await api.delete(`/sub-dealers/${branchId}/ledger/${id}`);
      toast.success("Entry removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Inline preview — fetches the invoice and opens a drawer showing the
  // per-vehicle bucket breakdown without printing. Useful when the operator
  // just wants to inspect what was billed.
  const viewInvoice = async (invoiceId) => {
    try {
      const { data } = await api.get(`/sub-dealers/invoices/${invoiceId}`);
      setPreviewInvoice(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  /**
   * Soft-cancel an AD invoice — admin only, ≤2 days old, vehicles not re-sold.
   * Posts a CREDIT_NOTE reversal to the ledger and moves vehicles back to
   * Stock Yard. Reloads the page state on success.
   */
  const cancelInvoice = async (invoiceId, invoiceNo) => {
    const reason = window.prompt(`Cancel invoice ${invoiceNo}?\n\nThis will (1) mark the invoice as cancelled, (2) post a reversing credit entry to the ledger, and (3) move the vehicles back to Stock Yard.\n\nEnter a reason (optional):`);
    if (reason === null) return; // user clicked Cancel
    try {
      await api.post(`/sub-dealers/invoices/${invoiceId}/cancel`, { reason: reason || "" });
      toast.success(`Invoice ${invoiceNo} cancelled · ledger reversed · vehicles back to Stock Yard`);
      setPreviewInvoice(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  /**
   * Return ONE line (vehicle) from a sub-dealer invoice back to Stock Yard.
   * Distinct from `cancelInvoice` which cancels the whole invoice — this
   * detaches only the specified VIN. Sub-dealer's ledger gets a partial
   * CREDIT_NOTE, vehicle status flips back to available.
   */
  const returnInvoiceLine = async (invoiceId, invoiceNo, vehicleId, vin) => {
    const reason = window.prompt(
      `Return VIN ${vin} from invoice ${invoiceNo}?\n\n` +
      `This will (1) mark this ONE line as returned (other vehicles stay billed), ` +
      `(2) credit the sub-dealer's ledger for this line's amount, and (3) move the ` +
      `vehicle back to Stock Yard so you can re-sell it.\n\nEnter a reason (optional):`
    );
    if (reason === null) return;

    const post = async (force) => api.post(`/sub-dealers/invoices/${invoiceId}/return-line`, {
      vehicle_id: vehicleId || null,
      vin: vin || null,
      reason: reason || "",
      force,
    });

    try {
      let res;
      try {
        res = await post(false);
      } catch (err) {
        // 409 → the backend needs the admin to confirm because the vehicle
        // is no longer in the sub-dealer's branch (historical invoice) or
        // is already sold/registered. Surface the details, ask for
        // confirmation, and retry with force=true.
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 409 && detail && detail.needs_confirmation) {
          const msg =
            `⚠ Vehicle state check\n\n${detail.message}\n\n` +
            `Current status: ${detail.current_status || "—"}\n` +
            `Current branch: ${detail.current_branch_name || "—"}\n\n` +
            `Proceed with a FORCED return anyway?\n` +
            `(The ledger will still be credited. Physical stock will only move ` +
            `if the vehicle isn't already sold/registered.)`;
          if (!window.confirm(msg)) return;
          res = await post(true);
        } else {
          throw err;
        }
      }
      const data = res.data;
      toast.success(`Line returned · ₹${(data.credited_amount || 0).toLocaleString("en-IN")} credited to ledger`);
      setPreviewInvoice(data);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  /**
   * Admin sign-off on a pending credit-direction ledger entry. The entry
   * starts contributing to the running balance from this point onward.
   */
  const approveEntry = async (entryId) => {
    if (!window.confirm("Approve this credit-note entry?\n\nIt will start contributing to the sub-dealer's running balance.")) return;
    try {
      await api.post(`/sub-dealers/${branchId}/ledger/${entryId}/approve`, { note: "" });
      toast.success("Credit entry approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /** Reject a pending credit-direction ledger entry — hard-deletes it. */
  const rejectEntry = async (entryId) => {
    const reason = window.prompt("Reject this credit-note entry? Provide a reason (optional):");
    if (reason === null) return;
    try {
      await api.post(`/sub-dealers/${branchId}/ledger/${entryId}/reject`, { note: reason || "" });
      toast.success("Credit entry rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Shared by both the Invoices tab and the ledger-row "view invoice" button.
  // Fetches the full invoice (with hydrated lines / customer / branch), renders
  // it through the existing tax-invoice template, and opens a new window. The
  // operator can hit Cmd/Ctrl+P from there to print.
  const openInvoice = async (invoiceId) => {
    try {
      const { data } = await api.get(`/sub-dealers/invoices/${invoiceId}`);
      const w = window.open("", "_blank");
      if (!w) {
        toast.error("Popup blocked — allow popups for this site.");
        return;
      }
      w.document.open();
      w.document.write(buildSubDealerTaxInvoiceHtml(data));
      w.document.close();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="sub-dealer-detail">
      <PageHeader
        eyebrow={`10 / Sub-Dealers / ${branch?.name || ""}`}
        title={branch?.name || "Sub-Dealer"}
        subtitle={`Code: ${branch?.code || ""} · Outstanding: ${fmtMoney(ledger.closing_balance)}`}
        actions={
          <button
            onClick={() => nav("/sub-dealers")}
            className="px-3 py-2 border border-canvas-line text-xs font-semibold hover:border-ink inline-flex items-center gap-2"
          >
            <ArrowLeft size={13} weight="bold" /> All Sub-Dealers
          </button>
        }
      />
      <div className="px-8 py-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-canvas-line bg-canvas-card p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Opening</div>
            <div className="font-display text-lg font-bold tabular-nums">{fmtMoney(ledger.opening_balance)}</div>
          </div>
          <div className="border border-canvas-line bg-canvas-card p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Total Debits</div>
            <div className="font-display text-lg font-bold tabular-nums text-destructive">{fmtMoney(ledger.debit_total)}</div>
          </div>
          <div className="border border-canvas-line bg-canvas-card p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Total Credits</div>
            <div className="font-display text-lg font-bold tabular-nums text-[#00713a]">{fmtMoney(ledger.credit_total)}</div>
          </div>
          <div className="border border-ink bg-canvas-card p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Outstanding</div>
            <div className="font-display text-lg font-bold tabular-nums" data-testid="sub-dealer-balance">{fmtMoney(ledger.closing_balance)}</div>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex border border-canvas-line w-fit">
            {[{ v: "ledger", l: "Ledger" }, { v: "invoices", l: "Invoices" }].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setTab(opt.v)}
                data-testid={`detail-tab-${opt.v}`}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-wider ${tab === opt.v ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-from" className="px-2 py-1.5 bg-canvas border border-canvas-line text-xs" />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-to" className="px-2 py-1.5 bg-canvas border border-canvas-line text-xs" />
            <button onClick={() => setShowEntry(true)} data-testid="open-add-entry" className="px-3 py-1.5 border border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white inline-flex items-center gap-1">
              <Plus size={11} weight="bold" /> Entry
            </button>
            <button onClick={() => setShowBulk(true)} data-testid="open-bulk" className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand inline-flex items-center gap-1">
              <FileText size={11} weight="bold" /> Bill Vehicles
            </button>
            <button onClick={() => setShowInvoice(true)} data-testid="open-invoice" className="px-3 py-1.5 border border-canvas-line text-ink text-xs hover:border-ink inline-flex items-center gap-1" title="Manual non-vehicle invoice">
              <FileText size={11} weight="bold" /> Manual Invoice
            </button>
          </div>
        </div>

        {tab === "ledger" && (
          <div className="bg-canvas-card border border-canvas-line overflow-x-auto">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead className="border-b border-canvas-line bg-canvas-panel">
                <tr>
                  {["Date", "Type", "Reference", "Debit ₹", "Credit ₹", "Running ₹", "Comments", ""].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ledger.rows.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-10 text-center font-mono text-xs text-ink-subtle">No ledger entries in this window.</td></tr>
                )}
                {ledger.rows.map((r) => {
                  const pending = !!r.pending_approval;
                  return (
                  <tr key={r.id} data-testid={`ledger-row-${r.id}`} className={`border-b border-canvas-line last:border-b-0 ${pending ? "bg-[#FFF6E5]" : ""}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {fmtDate(r.date)}
                      {pending && (
                        <span className="block mt-0.5 px-1 py-0.5 border border-[#B67100] text-[#B67100] text-[8px] font-mono uppercase tracking-wider w-fit">
                          PENDING
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-canvas-line">{r.txn_type_label || r.txn_type_name}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.reference_number || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-destructive">{r.direction === "debit" ? fmtMoney(r.amount) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-[#00713a]">{r.direction === "credit" ? fmtMoney(r.amount) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">
                      {pending ? <span className="text-ink-subtle italic text-[10px]">—not yet posted—</span> : fmtMoney(r.running_balance)}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">{r.comments || ""}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        {pending && isAdmin && (
                          <>
                            <button onClick={() => approveEntry(r.id)} data-testid={`approve-entry-${r.id}`} title="Approve credit note" className="text-ink-subtle hover:text-[#00713a]">
                              <Check size={12} weight="bold" />
                            </button>
                            <button onClick={() => rejectEntry(r.id)} data-testid={`reject-entry-${r.id}`} title="Reject credit note" className="text-ink-subtle hover:text-destructive">
                              <XIcon size={11} weight="bold" />
                            </button>
                          </>
                        )}
                        {r.invoice_id && (
                          <>
                            <button
                              onClick={() => viewInvoice(r.invoice_id)}
                              data-testid={`view-ledger-invoice-${r.id}`}
                              title="View invoice details"
                              className="text-ink-subtle hover:text-ink"
                            >
                              <Eye size={12} weight="bold" />
                            </button>
                            <button
                              onClick={() => openInvoice(r.invoice_id)}
                              data-testid={`print-ledger-invoice-${r.id}`}
                              title="View / Print Invoice"
                              className="text-ink-subtle hover:text-ink"
                            >
                              <Printer size={11} weight="bold" />
                            </button>
                          </>
                        )}
                        {!r.invoice_id && !pending && (
                          <button onClick={() => deleteEntry(r.id)} data-testid={`delete-entry-${r.id}`} className="text-ink-subtle hover:text-destructive"><Trash size={11} weight="bold" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "invoices" && (
          <div className="bg-canvas-card border border-canvas-line overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead className="border-b border-canvas-line bg-canvas-panel">
                <tr>
                  {["Invoice No", "Date", "Lines", "GST", "Grand Total ₹", "Status", ""].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-10 text-center font-mono text-xs text-ink-subtle">No invoices yet.</td></tr>
                )}
                {invoices.map((inv) => {
                  const cancelled = inv.status === "cancelled";
                  // Cancellable when: invoice not already cancelled, ≤2 days
                  // old, admin role. Backend re-validates all of this.
                  let ageDays = 999;
                  try {
                    const d = new Date(inv.date);
                    ageDays = Math.floor((Date.now() - d.getTime()) / 86400000);
                  } catch { /* keep ageDays = 999 */ }
                  const canCancel = isAdmin && !cancelled && ageDays <= 2;
                  return (
                    <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className={`border-b border-canvas-line last:border-b-0 ${cancelled ? "opacity-60" : ""}`}>
                      <td className="px-3 py-2 font-mono font-semibold">{inv.invoice_no}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{fmtDate(inv.date)}</td>
                      <td className="px-3 py-2">{inv.lines?.length || 0}</td>
                      <td className="px-3 py-2 font-mono text-[11px] uppercase">{inv.gst_type}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">
                        {cancelled ? <s>{fmtMoney(inv.grand_total)}</s> : fmtMoney(inv.grand_total)}
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] uppercase">
                        {cancelled
                          ? <span className="inline-block px-1.5 py-0.5 border border-destructive text-destructive">CANCELLED</span>
                          : inv.status}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          <button
                            onClick={() => viewInvoice(inv.id)}
                            data-testid={`view-invoice-${inv.id}`}
                            title="View invoice details"
                            className="text-ink-subtle hover:text-ink"
                          >
                            <Eye size={13} weight="bold" />
                          </button>
                          <button
                            onClick={() => openInvoice(inv.id)}
                            data-testid={`print-invoice-${inv.id}`}
                            title="Print Tax Invoice"
                            className="text-ink-subtle hover:text-ink"
                          >
                            <Printer size={13} weight="bold" />
                          </button>
                          {canCancel && (
                            <button
                              onClick={() => cancelInvoice(inv.id, inv.invoice_no)}
                              data-testid={`cancel-invoice-${inv.id}`}
                              title="Cancel invoice (admin, ≤2 days)"
                              className="text-ink-subtle hover:text-destructive"
                            >
                              <XIcon size={13} weight="bold" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {showEntry && (
          <AddEntryDialog
            branchId={branchId}
            txnTypes={txnTypes}
            onClose={() => setShowEntry(false)}
            onSaved={() => { setShowEntry(false); load(); }}
          />
        )}
        {showInvoice && (
          <InvoiceDialog
            branchId={branchId}
            onClose={() => setShowInvoice(false)}
            onSaved={() => { setShowInvoice(false); load(); }}
          />
        )}
        {showBulk && (
          <BulkVehicleDialog
            branchId={branchId}
            onClose={() => setShowBulk(false)}
            onSaved={() => { setShowBulk(false); load(); }}
          />
        )}
        {previewInvoice && (
          <InvoicePreviewDialog
            invoice={previewInvoice}
            onClose={() => setPreviewInvoice(null)}
            onPrint={() => { openInvoice(previewInvoice.id); }}
            onReturnLine={(vehicleId, vin) =>
              returnInvoiceLine(previewInvoice.id, previewInvoice.invoice_no, vehicleId, vin)
            }
            canReturn={user?.role === "admin"}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Inline preview drawer for a Sub-Dealer invoice. Shows:
 *  • Invoice meta (no, date, GST type)
 *  • Per-vehicle table with bucket breakdown columns
 *  • Composition totals (Ex-showroom / Other / Accessories taxable+gst)
 *  • Print button — opens the printable A4 invoice in a new tab
 *
 * Avoids re-rendering the full A4 layout — this is the operator's quick-look
 * view, not the printable artefact.
 */
function InvoicePreviewDialog({ invoice, onClose, onPrint, onReturnLine, canReturn }) {
  const inv = invoice || {};
  const lines = inv.lines || [];
  const isIntra = inv.gst_type === "intra";
  const isCancelled = inv.status === "cancelled";

  const totals = lines.reduce(
    (acc, ln) => ({
      ex: acc.ex + (Number(ln.ex_showroom_taxable) || 0),
      other: acc.other + (Number(ln.other_taxable) || 0),
      acc_incl: acc.acc_incl + (Number(ln.accessories_inclusive) || 0),
      acc_tax: acc.acc_tax + (Number(ln.accessories_taxable) || 0),
      acc_gst: acc.acc_gst + (Number(ln.accessories_gst) || 0),
    }),
    { ex: 0, other: 0, acc_incl: 0, acc_tax: 0, acc_gst: 0 },
  );
  const hasBucketData =
    totals.ex > 0 || totals.other > 0 || totals.acc_incl > 0;
  const accPct = lines.find((ln) => Number(ln.acc_gst_pct) > 0)?.acc_gst_pct || 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="invoice-preview-dialog">
      <div className="bg-canvas-card border border-canvas-line max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-start justify-between border-b border-canvas-line pb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">Invoice Preview</div>
            <div className="font-display text-2xl font-bold mt-1">{inv.invoice_no || "—"}</div>
            <div className="text-xs text-ink-muted mt-1">
              {inv.date} · {isIntra ? "Intra-state" : "Inter-state"} ·
              {isIntra ? ` CGST ${inv.cgst_pct}% + SGST ${inv.sgst_pct}%` : ` IGST ${inv.igst_pct}%`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onPrint} className="px-3 py-1.5 bg-ink text-white text-xs font-semibold flex items-center gap-1.5" data-testid="invoice-preview-print">
              <Printer size={12} weight="bold" /> Print
            </button>
            <button onClick={onClose} className="px-3 py-1.5 border border-canvas-line text-xs" data-testid="invoice-preview-close">Close</button>
          </div>
        </div>

        {/* Per-vehicle line items */}
        <div className="border border-canvas-line overflow-x-auto">
          <table className="w-full text-xs" data-testid="invoice-preview-lines">
            <thead className="bg-canvas-panel border-b border-canvas-line">
              <tr>
                {["#", "Model", "VIN", "Ex-showroom", "Other", "Accessories (incl.)", "Taxable", "GST", "Total", ""].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => (
                <tr key={ln.vehicle_id || i} className={`border-b border-canvas-line last:border-b-0 ${ln.returned_at ? "opacity-50 line-through" : ""}`}>
                  <td className="px-2 py-1.5 font-mono">{i + 1}</td>
                  <td className="px-2 py-1.5">{[ln.package_name || ln.model, ln.color].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{ln.vin || "—"}</td>
                  <td className="px-2 py-1.5 font-mono tabular-nums">{fmtMoney(Number(ln.ex_showroom_taxable) || 0)}</td>
                  <td className="px-2 py-1.5 font-mono tabular-nums">{fmtMoney(Number(ln.other_taxable) || 0)}</td>
                  <td className="px-2 py-1.5 font-mono tabular-nums">{fmtMoney(Number(ln.accessories_inclusive) || 0)}</td>
                  <td className="px-2 py-1.5 font-mono tabular-nums font-semibold">{fmtMoney(Number(ln.subtotal) || 0)}</td>
                  <td className="px-2 py-1.5 font-mono tabular-nums text-ink-muted">{fmtMoney((Number(ln.cgst) || 0) + (Number(ln.sgst) || 0) + (Number(ln.igst) || 0))}</td>
                  <td className="px-2 py-1.5 font-mono tabular-nums font-semibold">{fmtMoney(Number(ln.total) || 0)}</td>
                  <td className="px-2 py-1.5">
                    {ln.returned_at ? (
                      <span
                        className="px-1.5 py-0.5 border border-amber-500 text-amber-700 text-[9px] font-mono uppercase"
                        title={`Returned ${new Date(ln.returned_at).toLocaleString()} · ${ln.return_reason || ""}`}
                      >
                        Returned
                      </span>
                    ) : canReturn && !isCancelled && onReturnLine ? (
                      <button
                        type="button"
                        onClick={() => onReturnLine(ln.vehicle_id, ln.vin)}
                        data-testid={`return-invoice-line-${ln.vehicle_id}`}
                        title="Return this vehicle to Stock Yard (credit sub-dealer for this line)"
                        className="px-2 py-1 border border-amber-600 text-amber-700 text-[10px] font-mono uppercase hover:bg-amber-600 hover:text-white"
                      >
                        Return
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Composition rollup */}
        {hasBucketData && (
          <div className="border border-canvas-line p-4 space-y-2" data-testid="invoice-preview-composition">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">Composition</div>
            <table className="w-full text-xs">
              <thead className="border-b border-canvas-line">
                <tr>
                  {["Component", "Taxable Value", "GST", "Inclusive"].map((h) => (
                    <th key={h} className="text-left px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-canvas-line">
                  <td className="px-2 py-1.5">Ex-showroom (no tax in pkg) — taxed at invoice rate</td>
                  <td className="px-2 py-1.5 font-mono tabular-nums">{fmtMoney(totals.ex)}</td>
                  <td className="px-2 py-1.5 font-mono text-ink-muted">on invoice</td>
                  <td className="px-2 py-1.5 font-mono text-ink-muted">—</td>
                </tr>
                {totals.other > 0 && (
                  <tr className="border-b border-canvas-line">
                    <td className="px-2 py-1.5">Other — taxed at invoice rate</td>
                    <td className="px-2 py-1.5 font-mono tabular-nums">{fmtMoney(totals.other)}</td>
                    <td className="px-2 py-1.5 font-mono text-ink-muted">on invoice</td>
                    <td className="px-2 py-1.5 font-mono text-ink-muted">—</td>
                  </tr>
                )}
                {totals.acc_incl > 0 && (
                  <tr>
                    <td className="px-2 py-1.5">Accessories — GST-inclusive @ {accPct}%</td>
                    <td className="px-2 py-1.5 font-mono tabular-nums">{fmtMoney(totals.acc_tax)}</td>
                    <td className="px-2 py-1.5 font-mono tabular-nums">{fmtMoney(totals.acc_gst)}</td>
                    <td className="px-2 py-1.5 font-mono tabular-nums">{fmtMoney(totals.acc_incl)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Grand totals */}
        <div className="border border-canvas-line p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Taxable</div>
            <div className="font-mono tabular-nums font-semibold mt-1">{fmtMoney(inv.subtotal || 0)}</div>
          </div>
          {isIntra ? (
            <>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">CGST</div>
                <div className="font-mono tabular-nums font-semibold mt-1">{fmtMoney(inv.cgst_total || 0)}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">SGST</div>
                <div className="font-mono tabular-nums font-semibold mt-1">{fmtMoney(inv.sgst_total || 0)}</div>
              </div>
            </>
          ) : (
            <div className="col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">IGST</div>
              <div className="font-mono tabular-nums font-semibold mt-1">{fmtMoney(inv.igst_total || 0)}</div>
            </div>
          )}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Grand total</div>
            <div className="font-mono tabular-nums font-bold text-lg mt-1" data-testid="invoice-preview-grand-total">{fmtMoney(inv.grand_total || 0)}</div>
            {Number(inv.company_discount_total) > 0 && (
              <div className="font-mono text-[9px] text-emerald-700 mt-0.5" data-testid="invoice-preview-discount">
                incl. −{fmtMoney(inv.company_discount_total)} company discount
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Public default export with route switch
// ============================================================
export default function SubDealersPage({ mode = "list" }) {
  const { branchId } = useParams();
  if (mode === "detail" || branchId) return <SubDealerDetail />;
  return <SubDealersList />;
}
